import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate  } from 'react-router-dom';

import { 
    TableCell, Table, TableBody, Box, TableRow, TableContainer, Card, CardContent, 
    Typography, Stack, Link, IconButton, Button, useMediaQuery, Grid, Menu, MenuItem, ListItemIcon 
} from '@mui/material';
import { Edit, Delete, NoteAdd, Image, MoreVert, OpenInNew, QrCode } from '@mui/icons-material';
import { useOrganizationContext } from '../../../context/organization_context/OrganizationContext';
import { useLoadingContext } from '../../../context/loading/LoadingContext';
import { ToastType, useToast } from '../../../context/toast/ToastContext';
import AppSidebar from '../../../components/navbar/DesktopAppSidebar';
import { useUserContext } from '../../../context/user_context/UserContext';
import { assetSnapshotListener, assetNoteSnapshotListener, deleteAsset } from '../../../repository/AssetDAO';
import { deleteAssetNote } from '../../../repository/NotesDAO';
import { formatFirestoreTimestampMMDDYYYY } from '../../../utils/time_utils/TimeUtils';
import EditAssetModal from './modals/EditAssetModal';
import AddAssetNoteModal from './modals/AddAssetNoteModal';
import ConfirmDeleteModal from '../../../components/modals/ConfirmDeleteModal';
import { Loading } from '../../../components/loading/Loading';
import usePermissions from '../../../utils/permissions/UsePermissions';
import { Modules } from '../../../utils/permissions/Modules';
import { PermissionTypes } from '../../../utils/permissions/PermissionTypes';
import { StringKeys } from '../../../resources/strings/StringKeys';
import { useTranslation } from 'react-i18next';
import ImageUploadModal from '../../../components/image_upload_modal/ImageUploadModal';
import { ImageUploadType } from '../../../components/image_upload_modal/ImageUploadType';
import ViewNoteModal from '../../../components/notes/ViewNoteModal';
import { useQRCodePrinter } from '../../../components/qr_code/QRCodePrinter';
import { handleMenuClick, handleMenuClose } from '../../../components/utils/MenuHandlers';

function AssetDetail() {

    const { assetId } = useParams();
    const { organizationEmployees } = useOrganizationContext();
    const { firebaseUser } = useUserContext();
    const { setLoadingData } = useLoadingContext();
    const { showToast } = useToast();
    const [assetData, setAssetData] = useState();
    const [noteData, setNoteData] = useState([]);
    const [showAssetToEditModal, setShowAssetToEditModal] = useState(false);
    const [showAddImageModal, setShowAddImageModal] = useState(false);
    const [showAddNoteModal, setShowAddNoteModal] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmNoteOpen, setConfirmNoteOpen] = useState(false);
    const [selectedNote, setSelectedNote] = useState(null); 
    const [noteModalOpen, setNoteModalOpen] = useState(false);
    const isMobile = useMediaQuery('(max-width:600px)');
    const { hasPermission } = usePermissions();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [anchorEl, setAnchorEl] = useState(null);
    const { printQRCode } = useQRCodePrinter();

    useEffect(() => {
        if (firebaseUser) {
            const unsubscribe = assetSnapshotListener(firebaseUser.organization, assetId, setAssetData);
            return () => unsubscribe();
        }
    }, [firebaseUser, assetId]);

    useEffect(() => {
        if (assetId && firebaseUser) {
            const unsubscribe = assetNoteSnapshotListener(firebaseUser.organization, assetId, setNoteData);
            return () => unsubscribe();
        }
    }, [assetId, firebaseUser]);

    const handleDeleteAsset = async () => {
        setLoadingData(true);
        try {
            const result = await deleteAsset(firebaseUser.organization, assetId);
            
            if (result.success) {
                showToast(t(StringKeys.SUCCESSFULLY_DELETED_X, { x: assetData.name }), ToastType.SUCCESS);
                navigate('/assets', { replace: true });
            } else {
                showToast(t(StringKeys.FAILED_TO_DELETE_X, { x: assetData.name }), ToastType.DANGER);
            }
        } catch (error) {
            console.log(error)
            showToast(t(StringKeys.FAILED_TO_DELETE_X, { x: assetData.name }), ToastType.DANGER);
        } finally {
            setLoadingData(false);
        }
    };
    

    const handleDeleteNote = async () => {
        if (!selectedNote) return;
        setLoadingData(true);
        try {
            var result = await deleteAssetNote(firebaseUser.organization, assetId, selectedNote.id);
            if(result.success) {
                showToast(t(StringKeys.SUCCESSFULLY_DELETED_NOTE), ToastType.SUCCESS);
            } else showToast(t(StringKeys.FAILED_TO_DELETE_NOTE), ToastType.DANGER);
        } catch (error) {
            showToast(t(StringKeys.FAILED_TO_DELETE_NOTE), ToastType.DANGER);
        } finally {
            setLoadingData(false);
            setSelectedNote(null);
        }
    };
    

    const handleNoteClick = (note) => {
        setSelectedNote(note);  
        setNoteModalOpen(true); 
    };

    const employee = organizationEmployees && assetData ? organizationEmployees.find(emp => emp.id === assetData.employee_id) : null;
    const employeeLink = employee ? (
        <Link
        onClick={() => {
            navigate(`/employees/${employee.id}`);
        }}
        sx={{ cursor: "pointer" }}
        >             
            {`${employee.first_name} ${employee.last_name}`}
        </Link>
    ) : t(StringKeys.NOT_ASSIGNED);

    const handlePrint = () => {
        printQRCode(assetData, 'assets');
    };

    if (!assetData) return <AppSidebar content={<Loading />} />;

    return (
        <>
            <AppSidebar
                content={
                    <>
                        <Stack gap={4}>
                            <Grid container alignItems="center" spacing={2}>
                                <Grid item>
                                    {assetData.image_url ? (
                                        <img 
                                            src={assetData.image_url} 
                                            alt={"Asset Image"} 
                                            style={{ width: isMobile ? '70px' : '80px', height: isMobile ? '70px' : '80px', cursor: "pointer" }} 
                                            onClick={() => setShowAddImageModal(true)}
                                        />
                                    ) : (
                                        <IconButton onClick={() => hasPermission(Modules.ASSETS, PermissionTypes.WRITE) && setShowAddImageModal(true)}>
                                            <Image fontSize="large" />
                                        </IconButton>
                                    )}
                                </Grid>
                                {!isMobile && (
                                    <Grid item>
                                        <Stack direction="row" spacing={1}>
                                            {hasPermission(Modules.ASSETS, PermissionTypes.WRITE) && (
                                                <>
                                                    <Button 
                                                        variant="text" 
                                                        startIcon={<Edit />} 
                                                        onClick={() => setShowAssetToEditModal(true)}
                                                        sx={{
                                                            textTransform: 'none',
                                                            fontSize: '1rem',
                                                        }}
                                                    >
                                                        {t(StringKeys.EDIT)}
                                                    </Button>
                                                    <Button 
                                                        variant="text" 
                                                        startIcon={<NoteAdd />} 
                                                        onClick={() => setShowAddNoteModal(true)}
                                                        sx={{
                                                            textTransform: 'none',
                                                            fontSize: '1rem',
                                                        }}
                                                    >
                                                        {t(StringKeys.ADD_NOTE)}
                                                    </Button>
                                                    <Button 
                                                        variant="text" 
                                                        startIcon={<QrCode />} 
                                                        onClick={handlePrint}
                                                        sx={{
                                                            textTransform: 'none',
                                                            fontSize: '1rem',
                                                        }}
                                                    >
                                                        {t(StringKeys.PRINT_QR_CODE)}
                                                    </Button>
                                                    <Button 
                                                        variant="text" 
                                                        startIcon={<Delete />} 
                                                        onClick={() => setConfirmOpen(true)} 
                                                        sx={{
                                                            textTransform: 'none',
                                                            fontSize: '1rem',
                                                        }}
                                                    >
                                                        {t(StringKeys.DELETE)}
                                                    </Button>
                                                </>
                                            )}
                                        </Stack>
                                    </Grid>
                                )}
                                {isMobile && (
                                    <Grid item>
                                        <IconButton onClick={(event) => handleMenuClick(event, setAnchorEl)}>
                                            <MoreVert />
                                        </IconButton>
                                        <Menu
                                            anchorEl={anchorEl}
                                            open={Boolean(anchorEl)}
                                            onClose={() => handleMenuClose(setAnchorEl)}
                                        >
                                            {hasPermission(Modules.ASSETS, PermissionTypes.WRITE) && (
                                                <>
                                                    <MenuItem onClick={() => { setShowAssetToEditModal(true); handleMenuClose(setAnchorEl); } }>
                                                        <ListItemIcon>
                                                            <Edit fontSize="small" />
                                                        </ListItemIcon>
                                                        {t(StringKeys.EDIT)}
                                                    </MenuItem>
                                                    <MenuItem onClick={() => { setShowAddNoteModal(true); handleMenuClose(setAnchorEl); } }>
                                                        <ListItemIcon>
                                                            <NoteAdd fontSize="small" />
                                                        </ListItemIcon>
                                                        {t(StringKeys.ADD_NOTE)}
                                                    </MenuItem>
                                                    <MenuItem onClick={() => { handlePrint(); handleMenuClose(setAnchorEl); }}>
                                                        <ListItemIcon>
                                                            <QrCode fontSize="small" />
                                                        </ListItemIcon>
                                                        {t(StringKeys.PRINT_QR_CODE)}
                                                    </MenuItem>
                                                    <MenuItem onClick={() => { setConfirmOpen(true); handleMenuClose(setAnchorEl); } }>
                                                        <ListItemIcon>
                                                            <Delete fontSize="small" />
                                                        </ListItemIcon>
                                                        {t(StringKeys.DELETE)}
                                                    </MenuItem>
                                                </>
                                            )}
                                        </Menu>
                                    </Grid>
                                )}
                            </Grid>

                            <Card variant="outlined">
                                <CardContent>
                                    <Typography variant={isMobile ? "h6" : "h5"}>{assetData.name}</Typography>
                                    <Typography>{t(StringKeys.SERIAL_NUMBER)}: {assetData.serial_number}</Typography>
                                    <Typography>{t(StringKeys.COST)}: ${assetData.price}</Typography>
                                    <Typography>{t(StringKeys.PURCHASE_DATE)}: {formatFirestoreTimestampMMDDYYYY(assetData.purchase_date)}</Typography>
                                    <Typography>{t(StringKeys.CONDITION)}: {t(assetData.condition)}</Typography>
                                    <Typography>{t(StringKeys.EMPLOYEE_IN_POSSESSION)}: {employeeLink}</Typography>
                                </CardContent>
                            </Card>
                            <Card variant="outlined">
                                <CardContent>
                                    <Typography variant={isMobile ? "h6" : "h5"}>{t(StringKeys.NOTES)}</Typography>
                                        {noteData.length > 0 ? (
                                        <TableContainer key={"notes-table"}>
                                            <Table>
                                                <TableBody>
                                                    {noteData.map(note => (
                                                        <TableRow style={{ cursor: "pointer" }}>
                                                            <TableCell>
                                                                {formatFirestoreTimestampMMDDYYYY(note.date)} - {note.title}
                                                            </TableCell>
                                                            {hasPermission(Modules.ASSETS, PermissionTypes.WRITE) && (                                                            
                                                                <>
                                                                    <TableCell align="right">
                                                                        <Button
                                                                            variant="text"
                                                                            onClick={() => handleNoteClick(note)}
                                                                        >
                                                                            <OpenInNew />
                                                                    </Button>
                                                                </TableCell>
                                                                <TableCell>
                                                                        <Button
                                                                            variant="text"
                                                                            onClick={() => {
                                                                                setSelectedNote(note);
                                                                                setConfirmNoteOpen(true);
                                                                            } }
                                                                        >
                                                                            <Delete />
                                                                        </Button>
                                                                    </TableCell>
                                                                </>
                                                            )}
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                            )
                        :
                            (
                                <TableContainer key={"empty-notes-table"}>
                                    <Table aria-label="no-notes-table">
                                        <TableBody>
                                            <TableRow>
                                                <TableCell colSpan={3} align="center">
                                                    <Typography variant="body2">{t(StringKeys.NO_NOTES_TO_DISPLAY)}</Typography>
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )
                        }
                        </CardContent>
                        </Card>
                            <EditAssetModal
                                show={showAssetToEditModal}
                                onHide={() => setShowAssetToEditModal(false)}
                                asset={assetData}
                            />
                            <AddAssetNoteModal
                                show={showAddNoteModal}
                                onHide={() => setShowAddNoteModal(false)}
                                asset={assetData}
                            />
                            <ImageUploadModal
                                show={showAddImageModal}
                                onHide={() => setShowAddImageModal(false)}
                                organization={firebaseUser.organization}
                                modalTitle={t(StringKeys.ADD_NEW_IMAGE)}
                                itemId={assetId}
                                cropShape="rect"
                                uploadType={ImageUploadType.AssetImage}
                            />
                            <ConfirmDeleteModal
                                open={confirmOpen}
                                onClose={() => setConfirmOpen(false)}
                                onConfirm={() => { handleDeleteAsset(); setConfirmOpen(false); }}
                                item={assetData.name}
                            />
                            <ConfirmDeleteModal
                                open={confirmNoteOpen}
                                onClose={() => setConfirmNoteOpen(false)}
                                onConfirm={() => { handleDeleteNote(); setConfirmNoteOpen(false); }}
                                item="note"
                            />
                            <ViewNoteModal
                                open={noteModalOpen} 
                                onClose={() => setNoteModalOpen(false)} 
                                note={selectedNote}
                                onDelete={handleDeleteNote} 
                            />
                        </Stack>
                    </>
                }
            />
        </>
    );
}

export default AssetDetail;
